import React, { useContext } from 'react';
import {
  Box,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  PeopleOutlined as PeopleOutlinedIcon,
} from '@mui/icons-material';
import CardBase from 'components/CardBase';
import IconCircle from 'components/IconCircle';
import CreateProcessContext from 'shared/contexts/CreateProcessContext';
import { DICTIONARY } from 'shared/constants';
import IconRow from 'components/IconRow';
import IconRowCell from 'components/IconRowCell';

const panelStyle = {
  marginTop: '1rem',
  '& > .MuiPaper-root': {
    marginBottom: '1rem',
  },
};

const getDependentsTypeStyles = (theme: Theme) => ({
  card: {
    alignItems: 'center',
    borderRadius: theme.spacing(4),
    display: 'flex',
    justifyContent: 'space-around',
    minWidth: '150px',
    padding: '0',
  },
  icon: {
    border: `1px solid ${theme.palette.primary1}`,
    borderRadius: '50%',
    color: theme.palette.primary1,
  },
  disabled: {
    border: `1px solid ${theme.palette.text.disabled}`,
    color: theme.palette.text.disabled,
  },
  margin: {
    margin: `${theme.spacing(2)} 0`,
  },
});

const DependentsType: React.FC = () => {
  const theme = useTheme();
  const styles = getDependentsTypeStyles(theme);
  const {
    process,
    setProcess,
  } = useContext(CreateProcessContext);

  const handleSpouseSelection = (event: any) => {
    setProcess((draft) => {
      draft.employeeData!.spouse = event.target.value === 'true';
    });
  };

  const handleDependents = (dependents: number) => {
    setProcess((draft) => {
      draft.employeeData!.dependents = dependents;
    });
  };

  /* Min, Max for Number of Children */
  const min = 0;
  const max = 20;
  const disablePlus = process.employeeData?.dependents === max;
  const disableMinus = process.employeeData?.dependents === min
    || process.employeeData?.dependents === null
    || process.employeeData?.dependents === undefined;

  const handlePlus = () => {
    const dependents = process.employeeData?.dependents || 0;
    const value = dependents + 1;
    if (value <= max) {
      handleDependents(value);
    }
  };

  const handleMinus = () => {
    const dependents = process.employeeData?.dependents || 0;
    const value = dependents - 1;
    if (value >= min) {
      handleDependents(value);
    }
  };

  return (
    <Box sx={panelStyle}>
      <IconRow>
        <IconRowCell type="icon">
          <IconCircle Icon={PeopleOutlinedIcon} />
        </IconRowCell>
        <IconRowCell type="text">
          <Typography variant="h6">{DICTIONARY.IndicateDependents}</Typography>
        </IconRowCell>
      </IconRow>
      <IconRow>
        <IconRowCell type="text">
          <Grid container alignItems="flex-start">
            <Grid container size={6}>
              <Typography sx={styles.margin}>
                Do they have a spouse?
              </Typography>
              <RadioGroup
                value={String(process.employeeData?.spouse)}
                onChange={handleSpouseSelection}
              >
                <Grid container justifyContent="space-around">
                  <FormControlLabel
                    value="true"
                    control={<Radio color="primary" />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value="false"
                    control={<Radio color="primary" />}
                    label="No"
                  />
                </Grid>
              </RadioGroup>
            </Grid>
            <Grid container justifyContent="center" size={6}>
              <Typography sx={styles.margin}>
                How many children?
              </Typography>
              <CardBase sx={styles.card}>
                <IconButton onClick={handleMinus} disabled={disableMinus} size="large">
                  <RemoveIcon
                    sx={[styles.icon, disableMinus ? styles.disabled : {}]}
                  />
                </IconButton>
                <Typography>{process.employeeData?.dependents || 0}</Typography>
                <IconButton
                  onClick={handlePlus}
                  disabled={process.employeeData?.dependents === max}
                  size="large">
                  <AddIcon
                    sx={[styles.icon, disablePlus ? styles.disabled : {}]}
                  />
                </IconButton>
              </CardBase>
            </Grid>
          </Grid>
        </IconRowCell>
      </IconRow>
    </Box>
  );
};

export default DependentsType;
